import { useState, useEffect } from "react";
import { Bell, BellOff, Loader2, Check } from "lucide-react";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";

interface PriceDropAlertProps {
  productHandle: string;
  productTitle: string;
  currentPrice: number;
}

export const PriceDropAlert = ({ productHandle, productTitle, currentPrice }: PriceDropAlertProps) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkExistingAlert();
  }, [productHandle]);

  const checkExistingAlert = async () => {
    try {
      const supabase = await getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setChecking(false); return; }

      const { data } = await supabase
        .from('price_drop_alerts' as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('product_handle', productHandle)
        .eq('is_active', true)
        .maybeSingle();

      setIsSubscribed(!!data);
    } catch {
      // ignore
    } finally {
      setChecking(false);
    }
  };

  const toggleAlert = async () => {
    setLoading(true);
    try {
      const supabase = await getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Sign in to watch prices", { description: "Create an account to track prices" });
        setLoading(false);
        return;
      }

      if (isSubscribed) {
        await supabase
          .from('price_drop_alerts' as any)
          .delete()
          .eq('user_id', user.id)
          .eq('product_handle', productHandle);
        setIsSubscribed(false);
        toast.success("Removed from your price watch");
      } else {
        await supabase
          .from('price_drop_alerts' as any)
          .insert({
            user_id: user.id,
            product_handle: productHandle,
            product_title: productTitle,
            current_price: currentPrice,
            target_price: Math.round(currentPrice * 0.85 * 100) / 100,
            email: user.email || "",
          });
        setIsSubscribed(true);
        toast.success("Added to your price watch");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (checking) return null;

  return (
    <button
      onClick={toggleAlert}
      disabled={loading}
      className={`flex items-center gap-1.5 text-[12px] font-medium transition-colors ${
        isSubscribed
          ? "text-primary hover:text-primary/80"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : isSubscribed ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Bell className="h-3.5 w-3.5" />
      )}
      {isSubscribed ? "On your price watch" : "Watch this price"}
    </button>
  );
};
